import React from 'react'
import _ from 'lodash/fp'

import {
  VictoryAxis,
  VictoryLine,
  VictoryTooltip,
  VictoryChart,
  VictoryBar,
  VictoryTheme,
  VictoryScatter,
  VictoryVoronoiContainer
} from 'victory'

import colors from '@fe/ui/colors'

export const PointChart = ({ holes }) => {
  const data = _.flow(
    _.dropRightWhile({ shots: 0 }),
    _.map(hole => ({
      x: hole.holeOrder,
      y: hole.points,
      label: `Hole ${hole.holeOrder}: ${hole.points} point${hole.points !== 1 ? 's' : ''}`
    }))
  )(holes)
  const maxPoints = _.get('points', _.maxBy('points', holes))

  return (
    <VictoryChart
      theme={VictoryTheme.material}
      domain={{ x: [1, data.length], y: [0, maxPoints + 1] }}
      containerComponent={
        <VictoryVoronoiContainer
          voronoiDimension='x'
        />
      }
    >
      <VictoryAxis
        style={{
          ticks: { stroke: 'transparent', fill: 'transparent' },
          tickLabels: { stroke: 'transparent', fill: 'transparent' }
        }}
      />
      <VictoryAxis dependentAxis />
      <VictoryLine
        y={() => 2}
        samples={1}
        style={{
          data: { stroke: colors.trackStrokeRed, strokeDasharray: '5,5' }
        }}
      />
      <VictoryScatter
        interpolation='step'
        labelComponent={<VictoryTooltip />}
        data={data}
      />
    </VictoryChart>
  )
}

export const PointAvgChart = ({ holes, selected, onClick = () => {}, ...props }) => {
  const data = _.flow(
    _.dropRightWhile({ shots: 0 }),
    _.reduce((acc, hole) => {
      acc.sum += hole.points
      const diff = acc.sum - (hole.holeOrder * 2)
      const data = {
        holeId: hole.holeId,
        x: hole.holeOrder,
        y: diff,
        label: `Hole ${hole.holeOrder} difference: ${diff > 0 ? '+' + diff : diff}`
      }
      return { sum: acc.sum, holes: [...acc.holes, data] }
    }, { sum: 0, holes: [] }),
    _.get('holes')
  )(holes)
  const maxDiff = Math.max(0, _.getOr(0, 'y', _.maxBy('y', data)))
  const minDiff = Math.min(0, _.getOr(0, 'y', _.minBy('y', data)))

  const dataForSelected = _.find({ holeId: selected }, data)

  return (
    <VictoryChart
      padding={{ top: 24, right: 24, bottom: 24, left: 24 }}
      width={1000}
      theme={VictoryTheme.material}
      domain={{ x: [1, data.length], y: [minDiff - 1, maxDiff + 5] }}
      containerComponent={
        <VictoryVoronoiContainer
          voronoiDimension='x'
        />
      }
      {...props}
    >
      <VictoryAxis
        style={{
          ticks: { stroke: 'transparent', fill: 'transparent' },
          tickLabels: { stroke: 'transparent', fill: 'transparent' },
          axis: { stroke: 'transparent', fill: 'transparent' },
          grid: { stroke: 'white' }
        }}
      />
      <VictoryAxis
        style={{
          grid: { stroke: 'white' }
        }}
        dependentAxis
      />
      <VictoryLine
        y={() => 0}
        samples={1}
        style={{
          data: { stroke: colors.trackStrokeRed, strokeDasharray: '5,5' }
        }}
      />
      <VictoryLine
        labelComponent={<VictoryTooltip />}
        data={data}
        style={{
          data: { stroke: colors.primaryDarkGreen }
        }}
      />
      <VictoryScatter
        labelComponent={<VictoryTooltip />}
        data={data}
        style={{
          data: { fill: colors.primaryDarkGreen }
        }}
        size={5}
      />
      <VictoryScatter
        labelComponent={<VictoryTooltip />}
        size={7}
        data={[dataForSelected]}
        style={{
          data: { fill: colors.primaryLightGreen }
        }}
      />
      <VictoryScatter
        labelComponent={<VictoryTooltip />}
        data={data}
        style={{
          data: { stroke: 'transparent', fill: 'transparent' }
        }}
        size={15}
        events={[{
          childName: 'all',
          target: 'data',
          eventHandlers: {
            onClick: (_event, { datum }) => onClick(datum.holeId)
          }
        }]}
      />
    </VictoryChart>
  )
}

export const ParChart = ({ holes }) => {
  const data = _.flow(
    _.dropRightWhile({ shots: 0 }),
    _.groupBy('par'),
    _.mapValues(_.meanBy('shots')),
    _.transform.convert({ cap: false })((r, v, k) => {
      r.push({ x: Number(k), y: v })
    }, []),
    _.map.convert({ cap: false })((v, i) => ({
      ...v,
      fill: [
        colors.pieLightBlue,
        colors.pieNormalBlueTransparent,
        colors.pieNormalBlue
      ][i % 3],
      label: `Par ${v.x}: ${v.y.toFixed(2)}`
    }))
  )(holes)

  const extra = 0.5
  const minValue = _.getOr(1, 'y', _.minBy('y', data)) * extra
  const maxValue = _.getOr(1, 'y', _.maxBy('y', data)) * (1 + extra / 2)

  return (
    <VictoryChart theme={VictoryTheme.material} domain={{ x: [2, 6], y: [minValue, maxValue] }}>
      <VictoryAxis
        style={{
          ticks: { stroke: 'transparent', fill: 'transparent' },
          tickLabels: { stroke: 'transparent', fill: 'transparent' }
        }}
      />
      <VictoryAxis dependentAxis />
      <VictoryBar
        labelComponent={<VictoryTooltip />}
        data={data}
        style={{ data: { fill: d => d.fill } }}
      />
    </VictoryChart>
  )
}
